// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Agents browser on a machine's page in Settings → Connections → Agents (design/
// agents-browser set1-set3): what the browser may reach through the machine (this computer only,
// the default; its local network too; everything, with a plain warning), and who opened what through
// it in the last 30 days. Konvo keeps the list (host:port, who, when, how much; never pages) and holds
// the machine to the limit; so does the machine's bridge. For now only the machine's owner browses.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import styles from '@app/features/konvo_agents/components/AgentBrowserSettings.module.css';
import {ChoiceRow} from '@app/features/konvo_agents/components/AgentSettingsRows';
import {shareErrorMessage} from '@app/features/konvo_agents/components/AgentShareBits';
import {shortTime} from '@app/features/konvo_agents/components/AgentsCopy';
import AgentBrowser from '@app/features/konvo_agents/state/AgentBrowser';
import AgentShares from '@app/features/konvo_agents/state/AgentShares';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {
	AGENT_BROWSER_LIMITS,
	type AgentBrowserLimit,
	type AgentBrowserLogEntry,
	DEFAULT_BROWSER_LIMIT,
} from '@konvo/schema/src/domains/konvo/AgentBrowser';
import type {AgentMachineView} from '@konvo/schema/src/domains/konvo/Agents';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconAlertTriangle, IconHistory, IconWorld} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const LIMIT_NAMES = {
	machine: msg({message: 'This computer', comment: 'Agents Settings: the browser reaches only the machine itself.'}),
	lan: msg({
		message: 'Its local network',
		comment: 'Agents Settings: the browser reaches the machine and its network.',
	}),
	all: msg({message: 'Everything', comment: 'Agents Settings: the browser reaches anything, the internet too.'}),
};

const LIMIT_HINTS = {
	machine: msg({
		message: 'Dev servers like localhost:5173',
		comment: 'Agents Settings: what the choice This computer reaches.',
	}),
	lan: msg({
		message: 'Also other devices on its network',
		comment: 'Agents Settings: what the choice Its local network reaches.',
	}),
	all: msg({message: 'The internet too', comment: 'Agents Settings: what the choice Everything reaches.'}),
};

const LOG_KEY = 'konvo-agents-browser-log';

export const AgentMachineBrowser = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const [saving, setSaving] = useState(false);
	const limit: AgentBrowserLimit = machine.limits.browser ?? DEFAULT_BROWSER_LIMIT;
	const name = machine.name;
	// The machine's own cap (konvo-agents config), when it is narrower than Everything.
	const cap = machine.limits.browserCap;
	const capName = cap && cap !== 'off' && cap !== 'all' ? i18n._(LIMIT_NAMES[cap]) : null;
	const pick = async (next: AgentBrowserLimit) => {
		if (next === limit) return;
		setSaving(true);
		try {
			await AgentShares.saveMachineSettings(machine.id, {browser: next});
		} catch (error) {
			ToastCommands.error(shareErrorMessage(i18n, error));
		} finally {
			setSaving(false);
		}
	};
	const openLog = () => {
		const close = () => ModalCommands.popWithKey(LOG_KEY);
		ModalCommands.pushWithKey(
			ModalCommands.modal(() => <BrowserLog machine={machine} onClose={close} />),
			LOG_KEY,
		);
	};
	const off = cap === 'off';
	return (
		<>
			<ChoiceRow
				icon={<IconWorld size={18} stroke={1.7} />}
				title={t({message: 'Browser', comment: 'Agents Settings: row about the Agents browser on a machine.'})}
				description={
					off
						? t({
								message: `Off on ${name} itself (konvo-agents config there)`,
								comment:
									'Agents Settings: the machine’s owner turned the browser off on the machine. The name is the computer.',
							})
						: capName
							? t({
									message: `${name} itself allows at most ${capName}`,
									comment:
										'Agents Settings: the machine caps the browser itself. The name is the computer, then a choice.',
								})
							: t({message: 'What it can reach', comment: 'Agents Settings: what the Browser row sets.'})
				}
				value={
					off
						? t({message: 'Off', comment: 'Agents Settings: the browser is off on the machine.'})
						: i18n._(LIMIT_NAMES[limit])
				}
				disabled={saving}
				groups={(onClose) => [
					{
						items: off
							? []
							: AGENT_BROWSER_LIMITS.map((value) => ({
									label: i18n._(LIMIT_NAMES[value]),
									subtext: i18n._(LIMIT_HINTS[value]),
									selected: limit === value,
									onSelect: () => {
										onClose();
										void pick(value);
									},
								})),
					},
					{
						items: [
							{
								icon: <IconHistory size={16} stroke={1.8} />,
								label: t({
									message: 'What was opened, last 30 days',
									comment: 'Agents Settings: opens the list of who opened what through the machine.',
								}),
								onClick: () => {
									onClose();
									openLog();
								},
							},
						],
					},
				]}
				data-flx="konvo-agents.machine.browser"
			/>
			{limit === 'all' && !off && (
				<div className={styles.warn} data-flx="konvo-agents.machine.browser.warning">
					<IconAlertTriangle size={17} stroke={1.8} className={styles.warnIcon} />
					<span>
						<Trans comment="Agents Settings: warning under 'Everything'. {name} is the computer.">
							<b>Pages go out as this computer.</b> Sites see {name}'s internet address, and the browser reaches
							everything {name} can: its local network, VPN and Tailscale too.
						</Trans>
					</span>
				</div>
			)}
		</>
	);
});

function bytes(i18n: ReturnType<typeof useLingui>['i18n'], n: number): string {
	const units = ['B', 'KB', 'MB', 'GB'];
	let value = n;
	let unit = 0;
	while (value >= 1024 && unit < units.length - 1) {
		value /= 1024;
		unit++;
	}
	return `${new Intl.NumberFormat(i18n.locale, {maximumFractionDigits: unit && value < 10 ? 1 : 0}).format(value)} ${units[unit]}`;
}

/** Who opened what through the machine, the last 30 days, newest first. */
function BrowserLog({machine, onClose}: {machine: AgentMachineView; onClose: () => void}) {
	const {i18n, t} = useLingui();
	const [entries, setEntries] = useState<Array<AgentBrowserLogEntry> | null>(null);
	const [failed, setFailed] = useState(false);
	useEffect(() => {
		AgentBrowser.loadLog(machine.id).then(setEntries, () => setFailed(true));
	}, [machine.id]);
	const name = machine.name;
	const now = Date.now();
	return (
		<Modal.Root size="medium" centered onClose={onClose} data-flx="konvo-agents.machine.browser.log.dialog">
			<Modal.Header
				title={t({
					message: `Opened through ${name}`,
					comment: 'Agents Settings: title of the list of what was opened through the machine with the browser.',
				})}
				onClose={onClose}
			/>
			<Modal.Content>
				<p className={styles.logLead}>
					<Trans comment="Agents Settings: under the title of the browser list.">
						The last 30 days: which addresses were opened, by whom and how much came through. Konvo keeps no pages.
					</Trans>
				</p>
				{failed ? (
					<p className={styles.logEmpty}>
						<Trans comment="Agents Settings: the browser list did not load.">
							Konvo didn't answer. Try again in a minute.
						</Trans>
					</p>
				) : entries === null ? (
					<p className={styles.logEmpty}>
						<Trans comment="Agents Settings: the browser list is loading.">Loading…</Trans>
					</p>
				) : entries.length === 0 ? (
					<p className={styles.logEmpty} data-flx="konvo-agents.machine.browser.log.empty">
						<Trans comment="Agents Settings: nothing was opened through the machine yet.">Nothing opened yet.</Trans>
					</p>
				) : (
					<ul className={styles.logList} data-flx="konvo-agents.machine.browser.log.list">
						{entries.map((entry) => {
							const address = `${entry.host}:${entry.port}`;
							const who = entry.name;
							const when = shortTime(i18n, entry.lastAt, now);
							const down = bytes(i18n, entry.bytesDown);
							return (
								<li key={`${entry.at}|${address}`} className={styles.logRow}>
									<span className={styles.logAddress}>{address}</span>
									<span className={styles.logMeta}>
										{entry.refused > 0 && entry.opened === 0
											? t({
													message: `${who} · refused · ${when}`,
													comment:
														'Agents Settings: browser list row for an address that was not opened. {who} is a person, {when} like 08:50.',
												})
											: t({
													message: `${who} · ${down} · ${when}`,
													comment:
														'Agents Settings: browser list row. {who} is a person, {down} how much came through (e.g. 2.4 MB), {when} like 08:50.',
												})}
									</span>
								</li>
							);
						})}
					</ul>
				)}
			</Modal.Content>
		</Modal.Root>
	);
}
